import { css, cx } from "@emotion/css";
import { ActionButton, ActionButtonType, getProgressTrackerItemState, ProgressTracker, ProgressTrackerItem, StepCount } from "@octopusdeploy/design-system-components";
import { space, text } from "@octopusdeploy/design-system-tokens";
import { useState } from "react";
import * as React from "react";
import type { BusyState } from "~/components/BusyFromPromise/BusyFromPromise";
import DialogLayoutConnect from "~/components/Dialog/DialogLayoutConnect";
import CustomSaveDialogLayout from "~/components/DialogLayout/Custom/CustomSaveDialogLayout";
import { CustomFlexDialogContent, SmallDialogFrame } from "~/components/DialogLayout/Custom/index";
import type { DialogLayoutDispatchProps } from "~/components/DialogLayout/DialogLayout";
import KeyboardHandler, { Key } from "~/components/KeyboardHandler/index";
import { isAllowed, type PermissionCheckProps } from "~/components/PermissionCheck/PermissionCheck";
export interface TemplateWizardPageProps {
    content: React.ReactNode;
    title: string;
    additionalTitle?: React.ReactNode;
    statusBarLabel: string;
    additionalActions?: JSX.Element[];
    hideAdditionalActionsOnSinglePage?: boolean;
    actionAvailable: boolean;
    singlePageSaveLabel?: string;
    singlePageSavingLabel?: string;
}
export interface TemplateWizardDialogLayoutProps {
    savePermission?: PermissionCheckProps;
    onSaveClick(): Promise<boolean>;
    errors?: Error[] | null;
    busy?: BusyState | null;
    pages: TemplateWizardPageProps[];
}
function TemplateWizardDialogLayoutInternal({ savePermission, onSaveClick, busy, errors, pages, close }: TemplateWizardDialogLayoutProps & DialogLayoutDispatchProps) {
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [dialogResizeKey] = useState(new Date().getDate().toString());
    const pageCount = pages.length;
    const currentPage = pages[currentPageIndex];
    const finalPage = pages[pageCount - 1];
    const displayStatusBar = pageCount > 1;
    const saveActionAvailable = finalPage.actionAvailable;
    const saveClick = async () => {
        const result = await onSaveClick();
        if (result) {
            close();
        }
    };
    const isSaveButtonDisabled = (): boolean => {
        const disabledDueToPermission = !!savePermission ? !isAllowed(savePermission) : false;
        return !!(!saveActionAvailable || disabledDueToPermission || busy);
    };
    const canNext = () => currentPage.actionAvailable && currentPageIndex < pageCount - 1;
    const next = () => {
        if (canNext()) {
            setCurrentPageIndex(currentPageIndex + 1);
        }
    };
    const onEscKeyPressed = () => {
        close();
        return true;
    };
    const onCancel = () => {
        close();
    };
    const getSaveLabel = () => {
        if (pageCount === 1) {
            return finalPage.singlePageSaveLabel ?? "Done";
        }
        return "Done";
    };
    const getSaveBusyLabel = () => {
        if (pageCount === 1) {
            return finalPage.singlePageSavingLabel ?? "Saving...";
        }
        return "Saving...";
    };
    const saveActionButton = <ActionButton key="Save" type={ActionButtonType.Save} label={getSaveLabel()} busyLabel={getSaveBusyLabel()} onClick={saveClick} disabled={isSaveButtonDisabled()}/>;
    const cancelActionButton = <ActionButton key="Cancel" label="Cancel" disabled={busy} onClick={onCancel} type={ActionButtonType.Ternary}/>;
    const actions: JSX.Element[] = [];
    const nextActionButton = <ActionButton key="Next" label="Next" disabled={busy || !canNext()} onClick={next} type={ActionButtonType.Primary}/>;
    actions.push(cancelActionButton);
    for (const additionalAction of currentPage.additionalActions ?? []) {
        if (pageCount > 1 || !currentPage.hideAdditionalActionsOnSinglePage) {
            actions.push(additionalAction);
        }
    }
    if (currentPageIndex < pageCount - 1) {
        actions.push(nextActionButton);
    }
    else {
        actions.push(saveActionButton);
    }
    const keyboardRegistrations = [{ key: Key.Esc, onKeyPressed: onEscKeyPressed }];
    const child = pages[currentPageIndex].content;
    return (<CustomSaveDialogLayout frame={SmallDialogFrame} open={true} // This is set to true as it is required as a prop for this component. Although it is not used explicitly in it, it is passed as parameters to the render methods, but it is not needed in our case.
     close={close} onSaveClick={onSaveClick} renderTitle={() => {
            return displayStatusBar ? (<ProgressTracker className={progressTrackerStyles.wrapper}>
                        {pages.map((page, index) => (<ProgressTrackerItem key={page.statusBarLabel ?? page.title} label={page.statusBarLabel ?? page.title} state={getProgressTrackerItemState(index, currentPageIndex)}/>))}
                    </ProgressTracker>) : (<></>);
        }} renderContent={() => (<CustomFlexDialogContent className={cx(contentStyles.wrapper, { [contentStyles.reduceTopMargin]: displayStatusBar })}>
                    <KeyboardHandler key={dialogResizeKey} registrations={keyboardRegistrations}>
                        <div className={contentStyles.titleContainer}>
                            <h2 className={contentStyles.title}>{pages[currentPageIndex].title}</h2>
                            {pages[currentPageIndex].additionalTitle}
                        </div>
                        {child}
                    </KeyboardHandler>
                </CustomFlexDialogContent>)} renderActions={() => <DialogActions currentStep={currentPageIndex + 1} totalSteps={pageCount} actions={actions}/>} busy={busy ? busy : undefined} errors={errors ? errors : undefined}></CustomSaveDialogLayout>);
}
const contentStyles = {
    wrapper: css({
        margin: `${space[32]} ${space[32]} 0 ${space[32]}`,
        padding: space[4], // Needed to allow for radio button overflow
    }),
    reduceTopMargin: css({
        marginTop: space[16],
    }),
    titleContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[8],
        marginBottom: space[32],
    }),
    title: css({
        font: text.heading.small,
    }),
};
const progressTrackerStyles = {
    wrapper: css({
        margin: `${space[32]} ${space[32]} 0 ${space[32]}`,
    }),
};
interface DialogActionsProps {
    currentStep: number;
    totalSteps: number;
    actions?: React.ReactNode;
    additionalActions?: React.ReactNode;
}
function DialogActions({ currentStep, totalSteps, actions, additionalActions }: DialogActionsProps) {
    return (<div className={actionStyles.actions}>
            {totalSteps > 1 ? <StepCount currentStep={currentStep} totalSteps={totalSteps} variant="text"/> : <></>}
            <div className={actionStyles.secondaryActions}>{additionalActions}</div>
            <div className={actionStyles.primaryActions}>{actions}</div>
        </div>);
}
const actionStyles = {
    actions: css({
        display: "flex",
        justifyContent: "space-between",
        flex: "0 0 auto",
        flexWrap: "wrap",
        margin: `${space[32]}`,
    }),
    primaryActions: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        gap: space[8],
    }),
    secondaryActions: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        gap: space[8],
    }),
};
const TemplateWizardDialogLayout = DialogLayoutConnect.to<TemplateWizardDialogLayoutProps>(TemplateWizardDialogLayoutInternal);
TemplateWizardDialogLayout.displayName = "TemplateWizardDialogLayout";
export default TemplateWizardDialogLayout;
